namespace Solovis.Reports {

    export interface DataSourceDefinition {
        name: string;
        path?: string;
        code?: string;        
        postProcess?: any;

        data?: any;

        customDataAsync?: any;
        dynamic?: any;
        parameterMap?: any;

        dependsOn?: any[];

        ref?: any;

        /**
         * Optional method that allows data source to receive the reportControl instance before its other methods are called. This will
         * be called once before each time the data source is processed.
         */
        setReportControl?: (reportControl: Solovis.Reports.ReportControl) => void;
    }

    /**
     * Data structure used by DataSourceCollection to track pending data sources to load.
     */
    export interface DataSourceHandle {
        dataSource: DataSourceDefinition;
        loaded: boolean;
        loadCount: number;
        cacheInfo?: any;
        packId?: string;

        error?: any;
        dataSet?: any;
    }

    export class DataSourceCollection {

        public dataSources: DataSourceDefinition[];
        public reportControl: ReportControl;
        public areAllDataSetsLoaded: boolean;
        public dataSourceHandles: DataSourceHandle[];

        private _portalServices: Solovis.Portal.IPortalServices;


        constructor(
            options: {
                dataSources: DataSourceDefinition[],                
                reportControl: ReportControl
            }
        ) {
            this.dataSources = options.dataSources ? options.dataSources : [];
            this.reportControl = options.reportControl ? options.reportControl : null;

            this._portalServices = this.reportControl.portalServices();

            this.areAllDataSetsLoaded = false;

            this.dataSourceHandles = [];

            for (let dataSource of this.dataSources) {                
                var handle: DataSourceHandle = {
                    dataSource: dataSource,
                    loaded: false,
                    loadCount: 0
                };

                this.dataSourceHandles.push(handle);
            }
        }


        public loadData(parameters: any, runCount: number): void {
            this.areAllDataSetsLoaded = false;

            var self = this;

            for (var i = 0; i < this.dataSourceHandles.length; i++) {
                var handle = this.dataSourceHandles[i];
                handle.loaded = false;
                handle.cacheInfo = null;
                handle.loadCount = runCount;
            }

            for (let handle of this.dataSourceHandles) {            
                var dataSource = handle.dataSource;
                var expectedLoadCount = handle.loadCount;

                try {
                    self._loadDataForDataSourceHandle(handle, dataSource, parameters, expectedLoadCount);
                }
                catch (ex) {
                    self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
                }
            }
        }

        public _processDataSourceHandleLoadException(handle: DataSourceHandle, ex, expectedLoadCount: number): void {
            if (expectedLoadCount == handle.loadCount) {
                console.log(ex);

                var message = typeof ex == "string" ? ex : ex.message;
                handle.error = { message: message };
                handle.dataSet = null;
                handle.cacheInfo = null;
                handle.loaded = true;
                this._notifySingleDataSetLoaded(handle.dataSource.name, expectedLoadCount);
            }
        }

        public _processDataSourceNotLoaded(handle: DataSourceHandle, expectedLoadCount: number): void {
            if (expectedLoadCount == handle.loadCount) {
                handle.dataSet = { _notLoaded: true };
                handle.error = null;
                handle.cacheInfo = null;
                handle.loaded = true;
                this._notifySingleDataSetLoaded(handle.dataSource.name, expectedLoadCount);
            }
        }

        public _processDynamicDataSourceLoad(handle: DataSourceHandle, parameters: any): void {
            var self = this;
            var expectedLoadCount = handle.loadCount;

            try {
                var processDynamic = function (more) {
                    try {
                        var dynamicDataSource = handle.dataSource.dynamic(parameters, more);
                        if (!dynamicDataSource) {
                            // dynamic data source not defined so this means do not load anything.
                            self._processDataSourceNotLoaded(handle, expectedLoadCount);
                        }
                        else {
                            if (dynamicDataSource.dynamic) {
                                throw "Dynamic data source cannot contain dynamic data source declaration.";
                            }

                            self._loadDataForDataSourceHandle(handle, dynamicDataSource, parameters, expectedLoadCount);
                        }
                    }
                    catch (ex) {
                        self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
                    }
                };

                self._loadDataSetDependencies(handle, handle.dataSource, expectedLoadCount, processDynamic);
            }
            catch (ex) {
                self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
            }
        }


        public _loadDataForDataSourceHandle(
            handle: DataSourceHandle,
            dataSource: DataSourceDefinition,
            parameters: any,
            expectedLoadCount: number
        ): void {
            var self = this;

            if (dataSource.setReportControl) {
                dataSource.setReportControl(this.reportControl);
            }

            var expectedLoadCount = handle.loadCount;
            var dataSourceName = handle.dataSource.name;

            if (dataSource.dynamic) {
                // We will process the dynamic data source and then re-enter this method with the dynamically created data source.
                this._processDynamicDataSourceLoad(handle, parameters);
                return;
            }

            if (dataSource.customDataAsync) {
                if (dataSource.data || dataSource.path) {
                    throw "data and path may not be used in combination with customDataAsync";
                }

                var processCustomDataAsync = function (more) {
                    try {
                        // Get parameter options to use.
                        var options;
                        if (dataSource.parameterMap) {
                            options = dataSource.parameterMap(parameters, more);
                        }
                        else {
                            options = parameters;
                        }

                        more.parameters = options;
                        more.originalParameters = parameters;

                        if (options === null) {
                            // Null options means we should not load data set, or provide any further processing.
                            if (expectedLoadCount == handle.loadCount) {
                                handle.dataSet = { _notLoaded: true };
                                handle.error = null;
                                handle.cacheInfo = null;
                                handle.loaded = true;
                                self._notifySingleDataSetLoaded(dataSourceName, expectedLoadCount);
                            }
                        }
                        else {
                            dataSource.customDataAsync(
                                function (result) {
                                    // We test for expectedLoadCount in case there were multiple load requests outstanding.
                                    if (expectedLoadCount == handle.loadCount) {
                                        try {
                                            if (result.dataSet) {
                                                var loadedDataSet = result.dataSet;

                                                if (dataSource.postProcess) {
                                                    loadedDataSet = dataSource.postProcess(loadedDataSet, more);
                                                }

                                                handle.dataSet = loadedDataSet;
                                                handle.error = null;
                                            }
                                            else if (result.error) {
                                                handle.error = result.error;
                                                handle.dataSet = null;
                                            }
                                            else {
                                                handle.error = "customDataAsync response must contain dataSet or error.";
                                                handle.dataSet = null;
                                            }

                                            handle.cacheInfo = null;
                                            handle.loaded = true;
                                        }
                                        finally {
                                            self._notifySingleDataSetLoaded(dataSourceName, expectedLoadCount);
                                        }
                                    }
                                },
                                more);
                        }
                    } catch (ex) {
                        Solovis.log(ex);

                        var message = typeof ex == "string" ? ex : ex.message;
                        handle.error = { message: message };
                        handle.dataSet = null;
                        handle.cacheInfo = null;
                        handle.loaded = true;
                        self._notifySingleDataSetLoaded(dataSourceName, expectedLoadCount);
                    }
                };

                if (dataSource.dependsOn) {
                    self._loadDataSetDependencies(handle, dataSource, expectedLoadCount, processCustomDataAsync);
                }
                else {
                    processCustomDataAsync({ dataSets: {} });
                }
            }
            else if (dataSource.path || dataSource.code) {
                var downloadDataSet = function (more) {
                    var pathOrCode = {
                        path: dataSource.path ? dataSource.path : null,
                        code: dataSource.code ? dataSource.code : null
                    };

                    try {
                        // Get parameter options to use.
                        var options;
                        if (dataSource.parameterMap) {
                            options = dataSource.parameterMap(parameters, more);
                        }
                        else {
                            options = parameters;
                        }

                        more.parameters = options;
                        more.originalParameters = parameters;

                        // Load data set from server.
                        if (options === null) {
                            // Null options means we should not load data set, or provide any further processing.
                            self._processDataSourceNotLoaded(handle, expectedLoadCount);
                        }
                        else {
                            var cachingOptions = self._getCachePolicyToUse(dataSource, more);

                            var invokeOptions = null;
                            var extraData: any = {};
                            if (self.reportControl && self.reportControl.reportCode) {
                                extraData.reportCode = self.reportControl.reportCode;
                            }
                            if (cachingOptions) {
                                extraData.cachingOptions = cachingOptions;
                            }
                            if (self.reportControl) {
                                let dataContextToUse = self.reportControl.getDataContextToUse();
                                if (dataContextToUse) {
                                    extraData.dataContext = dataContextToUse;
                                } 
                            }

                            var loadDataCallback = function (response, success) {
                                // We test for expectedLoadCount in case there were multiple load requests outstanding.
                                if (expectedLoadCount == handle.loadCount) {
                                    try {
                                        if (success) {
                                            var loadedDataSet = response.result.dataSet;

                                            if (dataSource.postProcess) {
                                                loadedDataSet = dataSource.postProcess(loadedDataSet, more);
                                            }

                                            handle.dataSet = loadedDataSet;
                                            handle.error = null;

                                            if (response.result.wasCached) {
                                                handle.cacheInfo = {
                                                    createdAt: response.result.createdAt
                                                };
                                            }

                                            handle.packId = response.result.packId;
                                        }
                                        else {
                                            handle.error = response.error;
                                            handle.cacheInfo = null;
                                            handle.packId = null;
                                            handle.dataSet = null;                                            
                                        }

                                        handle.loaded = true;

                                        self._notifySingleDataSetLoaded(dataSourceName, expectedLoadCount);
                                    }
                                    catch (ex) {
                                        self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
                                    }
                                }
                            };

                            self._portalServices.reportDataLoader().loadDataSetAsync(pathOrCode, options, loadDataCallback, invokeOptions, extraData);
                        }
                    }
                    catch (ex) {
                        self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
                    }
                };

                if (dataSource.dependsOn) {
                    self._loadDataSetDependencies(handle, dataSource, expectedLoadCount, downloadDataSet);
                }
                else {
                    downloadDataSet({ dataSets: {} });
                }
            }
            else if (dataSource.ref) {
                // Load data set by processing reference to another element.
                if (!self._loadDataSetReferenceForHandleIfAvailable(handle)) {
                    var handleReferencedDataSetLoad = null;
                    handleReferencedDataSetLoad = function (event, data) {
                        var elementName = data.elementName;
                        var dataSourceName = data.dataSourceName;
                        var loadCount = data.loadCount;

                        if (elementName == dataSource.ref.elementName && dataSourceName == dataSource.ref.dataSourceName) {
                            $(self.reportControl).unbind('elementDataSetLoaded', handleReferencedDataSetLoad);

                            self._loadDataSetReferenceForHandleIfAvailable(handle);
                        }
                    };

                    $(self.reportControl).bind('elementDataSetLoaded', handleReferencedDataSetLoad);
                }
            }
            else if (typeof dataSource.data !== "undefined") {

                var processFixedDataSet = function (more) {
                    try {
                        // Data is directly provided.
                        var loadedDataSet = dataSource.data;

                        // Get parameter options to use.
                        var newParameters;
                        if (dataSource.parameterMap) {
                            newParameters = dataSource.parameterMap(parameters, more);
                        }
                        else {
                            newParameters = parameters;
                        }

                        more.parameters = newParameters;
                        more.originalParameters = parameters;

                        if (dataSource.postProcess) {
                            loadedDataSet = dataSource.postProcess(loadedDataSet, more);
                        }

                        handle.dataSet = loadedDataSet;
                        handle.error = null;
                        handle.loaded = true;
                        self._notifySingleDataSetLoaded(dataSourceName, expectedLoadCount);
                    }
                    catch (ex) {
                        self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
                    }
                };

                if (dataSource.dependsOn) {
                    self._loadDataSetDependencies(handle, dataSource, expectedLoadCount, processFixedDataSet);
                }
                else {
                    processFixedDataSet({ dataSets: {} });
                }
            }
            else {
                throw "Data source must specify path, data, customDataAsync, or ref."
            }
        }


        public _getCachePolicyToUse(dataSource, more) {
            let activeRunOptions = this.reportControl.getActiveRunReportOptions();
            let doNotUseCache = !!(activeRunOptions && activeRunOptions.doNotUseCache);

            let transformCachePolicy = (cachePolicy) => {
                if (cachePolicy) {
                    if (doNotUseCache) {
                        // Turn off part where we will use cached data if we aren't supposed to use cached data.
                        // We will still allow result to be cached.
                        cachePolicy = Solovis.deepCopy(cachePolicy);
                        cachePolicy.allowCachedResponse = false;
                    }
                }

                return cachePolicy;
            };

            if (this.reportControl._cachePolicy && this.reportControl._cachePolicy.caching) {
                // A report level cache policy will override settings on individual data sources (allows report stacks to override cache settings).
                let cachePolicyToUse = this.reportControl._cachePolicy.caching();
                cachePolicyToUse = transformCachePolicy(cachePolicyToUse);

                return cachePolicyToUse;
            }
            else if (dataSource.caching) {
                let cachePolicyToUse = dataSource.caching(more);
                cachePolicyToUse = transformCachePolicy(cachePolicyToUse);

                return cachePolicyToUse;
            }

            return null;
        }


        public _loadDataSetDependencies(handle, dataSource, expectedLoadCount, successCallbackForMore) {
            // Load all dependencies and then call the success callback which takes the "more" argument with the dependencies set.       
            var self = this;

            try {
                var more = {
                    dataSets: {}
                };

                if (_.isEmpty(dataSource.dependsOn)) {
                    successCallbackForMore(more);
                }
                else {
                    var dependencyDataSources = new Solovis.Reports.DataSourceCollection({
                        reportControl: self.reportControl,
                        dataSources: dataSource.dependsOn
                    });
                    $(dependencyDataSources).bind("allDataSetsLoaded", function () {
                        if (dataSource.dependsOn) {
                            for (let dependency of dataSource.dependsOn) {
                                var dependencyHandle = dependencyDataSources._getDataSourceHandle(dependency.name);
                                more.dataSets[dependency.name] = dependencyHandle.dataSet;
                            }
                        }
                        
                        successCallbackForMore(more);
                    });

                    dependencyDataSources.loadData({}, expectedLoadCount);
                }
            }
            catch (ex) {
                self._processDataSourceHandleLoadException(handle, ex, expectedLoadCount);
            }
        }


        public _loadDataSetReferenceForHandleIfAvailable(handle) {
            var dataSource = handle.dataSource;

            if (dataSource.ref) {
                var expectedLoadCount = handle.loadCount;

                var originalHandle = this.reportControl._getDataSourceHandleForElement(dataSource.ref.elementName, dataSource.ref.dataSourceName);
                if (originalHandle) {
                    if (originalHandle.loadCount == handle.loadCount && originalHandle.loaded) {
                        try {
                            var loadedDataSet = originalHandle.dataSet;

                            if (loadedDataSet) {
                                if (dataSource.postProcess) {
                                    loadedDataSet = dataSource.postProcess(loadedDataSet);
                                }
                            }

                            handle.dataSet = loadedDataSet;
                            handle.error = originalHandle.error;
                            handle.loaded = originalHandle.loaded;

                            handle.errorInSourceRef = originalHandle.error ? true : false;
                        }
                        catch (ex) {
                            handle.dataSet = null;
                            handle.error = ex;
                            handle.loaded = true;
                            handle.errorInSourceRef = false;
                        }

                        this._notifySingleDataSetLoaded(dataSource.name, expectedLoadCount);

                        return true;
                    }
                }
            }

            return false;
        }


        public _areAllHandlesLoaded() {
            /// <summary>
            /// This returns true if all data source handles have finished loading their data sets.
            /// </summary>

            for (var i = 0; i < this.dataSourceHandles.length; i++) {
                var handle = this.dataSourceHandles[i];

                if (!handle.loaded) {
                    return false;
                }
            }

            return true;
        }


        public _getDataSourceHandle(dataSourceName) {
            for (var i = 0; i < this.dataSourceHandles.length; i++) {
                var handle = this.dataSourceHandles[i];

                if (handle.dataSource.name == dataSourceName) {
                    return handle;
                }
            }

            return null;
        }


        public _notifySingleDataSetLoaded(dataSourceName, loadCount) {
            var allLoaded = false;
            if (this._areAllHandlesLoaded()) {
                this.areAllDataSetsLoaded = true;
                allLoaded = true;
            }

            $(this).trigger("singleDataSetLoaded", { dataSourceName: dataSourceName, loadCount: loadCount });

            if (allLoaded) {
                this._notifyAllDataSetsLoaded();
            }
        }


        public _notifyAllDataSetsLoaded() {
            this.areAllDataSetsLoaded = true;
            $(this).trigger("allDataSetsLoaded");
        }


        public getDataSets() {
            var dataSets: any = {};

            for (let handle of this.dataSourceHandles) {
                dataSets[handle.dataSource.name] = handle.dataSet;

                if (handle.error) {
                    if (!dataSets.errors) {
                        dataSets.errors = {};
                    }

                    dataSets.errors[handle.dataSource.name] = handle.error;
                }

                if (handle.cacheInfo) {
                    if (!dataSets.cacheInfo) {
                        dataSets.cacheInfo = {};
                    }

                    dataSets.cacheInfo[handle.dataSource.name] = handle.cacheInfo;
                }
            }

            return dataSets;
        }
    }

}
